import React, { ReactNode } from 'react';
import { Theme, Typography, TypographyProps } from '@mui/material';
import { escapeSpecialChars } from '@utils/regex';

const markedStyle = {
  color: 'inherit',
  backgroundColor: ({ palette }: Theme) => `${palette.secondary.main}80`,
  padding: '2px 0px',
};

interface ITypographyHighlighter {
  text?: string;
  secondaryText?: ReactNode;
  highlightText?: string;
  highlightStyle?: object;
  sanitizedHtml?: string;
}

function createMarkup(content: string) {
  return { __html: content };
}

const CHAR_TAG_OPEN = '<';
const CHAR_TAG_CLOSE = '>';

function getHtmlWithTextHighlighted(initialHtml: string, highlightText: string, highlightStyle?: object): string {
  const styleString = Object.entries(highlightStyle || markedStyle)
    .map(([k, v]) => `${k.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}:${v}`)
    .join(';');
  let highlightedHtml = '';
  let matchParts: string[] = [] as string[];
  let tagsInMatch: string[] = [] as string[];

  const addPartsWithoutMarksToResult = () => {
    for (let i = 0; i < tagsInMatch.length; i += 1) {
      highlightedHtml += matchParts[i];
      highlightedHtml += tagsInMatch[i];
    }
  };

  const getMarkedText = (text: string): string => {
    return `<mark style="${styleString}">${text}</mark>`;
  };

  const addPartsWithMarksToResult = () => {
    for (let i = 0; i < tagsInMatch.length; i += 1) {
      highlightedHtml += getMarkedText(matchParts[i]);
      highlightedHtml += tagsInMatch[i];
    }
  };

  let partialMatch = '';
  let possibleTag = '';
  let inMatch = false;
  let inTag = false;

  initialHtml.split('').forEach((char) => {
    if (inTag && char === CHAR_TAG_OPEN) {
      if (highlightText.startsWith(`${matchParts.join('')}${possibleTag}`)) {
        matchParts[matchParts.length - 1] = `${matchParts[matchParts.length - 1]}${possibleTag}`;
        possibleTag = char;
        return;
      }
      if (highlightText === `${matchParts.join('')}${possibleTag}`) {
        addPartsWithMarksToResult();
        matchParts = [];
        tagsInMatch = [];
        highlightedHtml += getMarkedText(`${matchParts[matchParts.length - 1]}${possibleTag}`);
        possibleTag = char;
        return;
      }
      if (`${matchParts.join('')}${possibleTag}`.startsWith(highlightText)) {
        addPartsWithMarksToResult();
        highlightedHtml += getMarkedText(
          `${matchParts[matchParts.length - 1]}${possibleTag.slice(
            0,
            highlightText.slice(matchParts.join('').length).length,
          )}`,
        );
        highlightedHtml += possibleTag.slice(highlightText.slice(matchParts.join('').length).length);
        matchParts = [];
        tagsInMatch = [];
        possibleTag = char;
        return;
      }
      highlightedHtml += possibleTag;
      possibleTag = char;
      return;
    }
    if (inTag) {
      possibleTag += char;
      if (char === CHAR_TAG_CLOSE) {
        inTag = false;
        if (inMatch) {
          tagsInMatch.push(possibleTag);
        } else {
          highlightedHtml += possibleTag;
        }
        possibleTag = '';
      }
      return;
    }
    if (!inTag && char === CHAR_TAG_OPEN) {
      inTag = true;
      if (inMatch) {
        matchParts.push(partialMatch);
        partialMatch = '';
      }
      possibleTag = char;
      return;
    }

    if (inMatch && highlightText === `${matchParts.join('')}${partialMatch}${char}`) {
      addPartsWithMarksToResult();
      highlightedHtml += getMarkedText(`${partialMatch}${char}`);
      inMatch = false;
      partialMatch = '';
      matchParts = [];
      tagsInMatch = [];
      return;
    }
    if (inMatch && highlightText.startsWith(`${matchParts.join('')}${partialMatch}${char}`)) {
      partialMatch += char;
      return;
    }
    if (inMatch) {
      addPartsWithoutMarksToResult();
      highlightedHtml += partialMatch;
      matchParts = [];
      tagsInMatch = [];
    }
    if (highlightText.startsWith(char)) {
      inMatch = true;
      partialMatch = char;
    } else {
      highlightedHtml += char;
      inMatch = false;
      partialMatch = '';
    }
  });
  addPartsWithoutMarksToResult();
  highlightedHtml += partialMatch;
  highlightedHtml += possibleTag;
  return highlightedHtml;
}

export const TypographyHighlighter = React.forwardRef<HTMLDivElement, ITypographyHighlighter & TypographyProps>(
  (props, ref) => {
    const { text, secondaryText, highlightText, highlightStyle, sanitizedHtml, ...other } = props;

    if ((!text || typeof text !== 'string') && (!sanitizedHtml || typeof sanitizedHtml !== 'string')) {
      return null;
    }

    if (!text || typeof text !== 'string') {
      let highlightedHtml = sanitizedHtml as string;
      if (highlightText) {
        highlightedHtml = getHtmlWithTextHighlighted(highlightedHtml, highlightText, highlightStyle);
      }
      return <Typography ref={ref} {...other} dangerouslySetInnerHTML={createMarkup(highlightedHtml)} />;
    }

    if (!highlightText) {
      return (
        <Typography ref={ref} {...other}>
          {text}
        </Typography>
      );
    }

    const regex = new RegExp(escapeSpecialChars(highlightText), 'gi');
    const matches = text.match(regex);
    const parts = text.split(regex);

    return (
      <>
        <Typography
          ref={ref}
          {...other}
          sx={[
            {
              display: 'inline',
            },
            ...(Array.isArray(other.sx) ? other.sx : [other.sx]),
          ]}
        >
          {parts?.map((part: string, index: number) => {
            const markedText = matches ? matches[index] : null;
            const key = `${part}-${index}`;
            return (
              <React.Fragment key={key}>
                {parts[index]}
                {markedText ? <mark style={highlightStyle || markedStyle}>{markedText}</mark> : null}
              </React.Fragment>
            );
          })}
        </Typography>
        {secondaryText || null}
      </>
    );
  },
);

TypographyHighlighter.displayName = 'TypographyHighlighter';
